import { bind, play, setEnabled, type SoundName } from "cuelume";

/**
 * Capa sonora global de AcademIA.
 *
 * Cuelume sintetiza todo con Web Audio, sin assets ni red. Los componentes que necesitan un
 * sonido específico usan `data-cuelume-*`; el fallback delegado cubre botones y enlaces del
 * resto de la plataforma para que la experiencia no dependa de que cada pantalla se acuerde de
 * etiquetarlos.
 */
let initialized = false;
const SOUND_PREFERENCE_KEY = "academia-sound-enabled";
const CUE_SELECTOR =
  "[data-cuelume-hover],[data-cuelume-press],[data-cuelume-release],[data-cuelume-toggle]";
const INTERACTIVE_SELECTOR =
  "button:not(:disabled),a[href],[role='button']:not([aria-disabled='true']),input[type='button']:not(:disabled),input[type='submit']:not(:disabled)";

function getUntypedInteractive(target: EventTarget | null) {
  if (!(target instanceof Element)) return null;
  const interactive = target.closest<HTMLElement>(INTERACTIVE_SELECTOR);
  if (!interactive || interactive.closest(CUE_SELECTOR)) return null;
  return interactive;
}

export function getLandingSoundEnabled() {
  if (typeof window === "undefined") return true;
  return window.localStorage.getItem(SOUND_PREFERENCE_KEY) !== "false";
}

export function initLandingSound() {
  if (initialized || typeof document === "undefined") return;
  initialized = true;
  setEnabled(getLandingSoundEnabled());
  bind();

  document.addEventListener(
    "pointerdown",
    (event) => {
      if (getUntypedInteractive(event.target)) sfx("press");
    },
    { passive: true },
  );
  document.addEventListener(
    "pointerup",
    (event) => {
      if (getUntypedInteractive(event.target)) sfx("release");
    },
    { passive: true },
  );
  document.addEventListener("click", (event) => {
    // `detail === 0` identifica activación por teclado; el pointer ya recibió press/release.
    if (event.detail === 0 && getUntypedInteractive(event.target)) sfx("release");
  });
}

export function sfx(name: SoundName) {
  try {
    play(name);
  } catch {
    // Blocked autoplay / missing Web Audio → silent no-op by design.
  }
}

export function setLandingSoundEnabled(enabled: boolean) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(SOUND_PREFERENCE_KEY, String(enabled));
  }
  setEnabled(enabled);
}
